import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ThumbsDown, ThumbsUp } from 'lucide-react';

import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGetSearchFiltersDataQuery } from '../../../../../redux/services/search/searchApi';
import {
  selectIsSearch,
  selectLastSearchValues,
  selectTotalCount,
} from '../../../../../redux/features/dashboard/dashboardSelectors';
import {
  selectIsAdmin,
  selectUserToken,
} from '../../../../../redux/features/user/userSelectors';
import { useGetCitiesForCountryQuery } from '../../../../../redux/services/data/citiesApi';
import {
  setSortBy,
  showSearch,
} from '../../../../../redux/features/dashboard/dashboardSlice';
import { useGetAllAgentsQuery } from '../../../../../redux/services/agents/agentsApi';
import { useRequestLikes } from '../../../../../hooks/useRequestLikes';

import {
  mockSort,
  mockAdditional,
  getMockMore,
  initialSearchValuesNames,
  localisationSelectData,
  searchValuesNames,
  initialValues as searchFormInitialValues,
} from '../../../../../lib/mocks/search-mock';
import { SearchButtonBar } from '../../../../ui/SearchButtonBar';
import { SearchFormTemplate } from '../../searchFormComponents/SearchFormTemplate';
import { InputsTemplate } from '../../../inputs/InputsTemplate';
import { SearchFormFilters } from '../SearchFormFilters';
import { LoaderProgress } from '../../../../ui/LoaderProgress';
import { SearchFormClientInfo } from '../SearchFormClientInfo';
import { SearchFormInputs } from '../SearchFormInputs';
import { IconButtonTemplate } from '../../../../ui/buttons/IconButtonTemplate';
import { ButtonTemplate } from '../../../../ui/buttons/ButtonTemplate';

import {
  colors,
  EMPTY_ARRAY,
  errorMessages,
  fieldTypes,
  searchRequestSortButtons,
} from '../../../../../data/constants';
import {
  getDefaultValues,
  transformRequestToSearch,
  transformSortMock,
} from '../../../../../utils/data';
import { initialValues } from '../../../../../lib/mocks/add-request-mock';
import { generateSearchQueriesRealty } from '../../../../../utils/utils';

const Badge = ({ count, isActive }) => (
  <span
    className={`absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full text-[11px] font-semibold leading-[18px] text-center pointer-events-none bg-redColor text-white ${isActive ? 'ring-2 ring-white' : ''}`}
  >
    {count}
  </span>
);

// Resolve Formik values (IDs / scalars) against each field's own selectData
// so multi-selects render pre-selected chips on hydration.
const MULTISELECT_FIELD_TYPES = new Set([
  fieldTypes.multiselect,
  fieldTypes.multiselectGrouped,
  fieldTypes.multiselectDepedent,
]);
const SINGLE_SELECT_FIELD_TYPES = new Set([
  fieldTypes.select,
  fieldTypes.selectSearchable,
]);

const enrichMockWithFormValues = (mockList, values) =>
  mockList.map(field => {
    const raw = values?.[field.name];
    if (raw === undefined || raw === null || raw === '') return field;
    if (Array.isArray(raw) && raw.length === 0) return field;

    const options = field.selectData || [];

    if (MULTISELECT_FIELD_TYPES.has(field.fieldType)) {
      const asArray = Array.isArray(raw) ? raw : [raw];
      const defaultValue = asArray
        .map(v => {
          const id = typeof v === 'object' ? v?.value ?? v?.id : v;
          return options.find(o => o.value === id || o.id === id);
        })
        .filter(Boolean);
      return defaultValue.length ? { ...field, defaultValue } : field;
    }

    if (SINGLE_SELECT_FIELD_TYPES.has(field.fieldType)) {
      const id = typeof raw === 'object' ? raw?.value ?? raw?.id : raw;
      const match = options.find(o => o.value === id || o.id === id);
      return match ? { ...field, defaultValue: match } : field;
    }

    return field;
  });

export const SearchForm = ({
  showAdditionalFilters = false,
  setShowAdditionalFilters = () => {},
  showSort = false,
  setShowSort = () => {},
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get('requestId');
  const isSearchShown = useSelector(selectIsSearch);
  const userToken = useSelector(selectUserToken);
  const isAdmin = useSelector(selectIsAdmin);
  const lastSearchValues = useSelector(selectLastSearchValues);

  const { searchRequest, likeCount, dislikeCount } = useRequestLikes();
  const totalCount = useSelector(selectTotalCount);

  const prevRequestId = useRef(requestId);
  const hydratedForRequestId = useRef(null);
  const hasHydratedFromReduxRef = useRef(false);
  const settingsClickLockRef = useRef(0);

  const [isLoading, setIsLoading] = useState(true);
  const [resetKey, setResetKey] = useState(0);
  const [activeButton, setActiveButton] = useState(
    searchRequestSortButtons.all
  );

  const [removedCityId, setRemovedCityId] = useState(null);
  const citiesId = new Set();

  const [requestMockAdditional, setRequestMockAdditional] = useState(null);
  const [requestMockMore, setRequestMockMore] = useState(null);
  const [searchInitialValues, setSearchInitialValues] = useState(
    searchFormInitialValues
  );

  const {
    data: filters = {},
    isLoading: isFiltersLoading,
    error: filtersError,
  } = useGetSearchFiltersDataQuery(userToken);
  const {
    data: cities,
    isLoading: isCitiesLoading,
    error: citiesError,
  } = useGetCitiesForCountryQuery({
    country_id: 73, // France countryId
  });
  const { data: agentOptions = EMPTY_ARRAY, isFetching: isAgentsLoading } =
    useGetAllAgentsQuery(userToken, { skip: !isAdmin });

  const handleSettingsClick = () => {
    //mobile sometimes double cliks, hard stop with ref
    const now = Date.now();
    if (now - settingsClickLockRef.current < 100) return;
    settingsClickLockRef.current = now;
    setShowAdditionalFilters(prev => !prev);
  };

  const resetFormState = () => {
    setSearchInitialValues({ ...searchFormInitialValues });
    setRequestMockAdditional(null);
    setRequestMockMore(null);
    setResetKey(k => k + 1);
  };

  const handleResetClick = () => {
    resetFormState();
    navigate('/', { replace: true });
    dispatch(
      showSearch({
        isSearchVisible: isSearchShown,
        searchType: '',
        searchQuery: '',
        searchQueryRequest: '',
        searchFilters: '',
        searchLikeIds: null,
        searchLikeFilter: null,
        lastSearchValues: null,
      })
    );
  };

  const handleSubmit = async values => {
    if (values && values.nativeEvent && values._reactName) {
      console.warn('SyntheticBaseEvent detected. Submission aborted.');

      return;
    }
    setShowSort(true);
    const query = await generateSearchQueriesRealty(
      initialSearchValuesNames,
      localisationSelectData,
      searchValuesNames,
      values
    );
    dispatch(
      showSearch({
        isSearchVisible: true,
        searchQuery: values.search,
        searchFilters: query,
        searchLikeFilter: null,
        lastSearchValues: values,
      })
    );
  };

  const handleCityIdRemove = useCallback((valuesList, name, removedItem) => {
    if (removedItem) {
      setRemovedCityId(removedItem.value);
    }
  }, []);

  const handleSortSelection = useCallback(
    async selectedItem => {
      await dispatch(setSortBy(selectedItem.value));
    },
    [dispatch]
  );

  const cityOptions = useMemo(
    () =>
      cities?.map(city => ({
        ...city,
        value: city.id,
        title: city.name || city.title,
      })) ?? [],
    [cities]
  );

  // Keep values, extend with input required properties
  const filterOptions = useMemo(() => {
    if (!filters) return {};
    return Object.fromEntries(
      Object.entries(filters).map(([key, items]) => [
        key,
        items.map(item => ({ ...item, value: item.id })),
      ])
    );
  }, [filters]);

  const { inputsAdditional, inputsMore } = useMemo(() => {
    const additional = mockAdditional.map(field => {
      if (field.name === searchValuesNames.city) {
        return {
          ...field,
          selectData: cityOptions,
          handleSelection: handleCityIdRemove,
        };
      }
      if (filterOptions[field.name]) {
        return { ...field, selectData: filterOptions[field.name] };
      }
      return field;
    });
    const more = getMockMore(isAdmin).map(field => {
      if (field.name === searchValuesNames.user) {
        return { ...field, selectData: agentOptions };
      }
      if (filterOptions[field.name]) {
        return { ...field, selectData: filterOptions[field.name] };
      }
      return field;
    });
    return { inputsAdditional: additional, inputsMore: more };
  }, [cityOptions, agentOptions, filterOptions, handleCityIdRemove, isAdmin]);

  const inputsSort = useMemo(
    () => transformSortMock(mockSort, handleSortSelection),
    [handleSortSelection]
  );

  const activeInputsAdditional = requestMockAdditional ?? inputsAdditional;
  const activeInputsMore = requestMockMore ?? inputsMore;

  // Called once on initial load when searchRequest arrives — populates form inputs from request
  const handleRequestSearch = () => {
    const requestValues = transformRequestToSearch(
      searchRequest,
      initialValues
    );

    const enrichedMockAdditional = getDefaultValues(
      requestValues.initialValues,
      inputsAdditional,
      { cities }
    );
    const enrichedMockMore = getDefaultValues(
      requestValues.initialValues,
      inputsMore
    );

    setRequestMockAdditional(enrichedMockAdditional);
    setRequestMockMore(enrichedMockMore);
    setSearchInitialValues(requestValues.initialValues);
    setResetKey(k => k + 1);

    handleSubmit(requestValues.values);
    setActiveButton(searchRequestSortButtons.all);
  };

  const handleAllClick = formValues => {
    handleSubmit(formValues);
    setActiveButton(searchRequestSortButtons.all);
  };

  const handleLikeSortClick = type => {
    dispatch(showSearch({ isSearchVisible: true, searchLikeFilter: type }));
    setActiveButton(type);
  };

  useEffect(() => {
    if (!isSearchShown) {
      resetFormState();
    }
  }, [isSearchShown]);

  // Clear form when requestId is removed from URL (user reset or navigated away)
  useEffect(() => {
    if (prevRequestId.current && !requestId) {
      resetFormState();
      hydratedForRequestId.current = null;
    }
    prevRequestId.current = requestId;
  }, [requestId]);

  // Re-hydrate form from last submitted values on remount ("had query → left → returned").
  useEffect(() => {
    if (hasHydratedFromReduxRef.current) return;
    if (isLoading) return;
    hasHydratedFromReduxRef.current = true;

    if (requestId) return;
    if (!lastSearchValues) return;

    setSearchInitialValues(lastSearchValues);
    setRequestMockAdditional(
      enrichMockWithFormValues(inputsAdditional, lastSearchValues)
    );
    setRequestMockMore(enrichMockWithFormValues(inputsMore, lastSearchValues));
    setResetKey(k => k + 1);
    setShowSort(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  // Pre-fill and auto-submit search when request data arrives from URL param.
  useEffect(() => {
    if (
      requestId &&
      searchRequest &&
      !isLoading &&
      hydratedForRequestId.current !== requestId
    ) {
      hydratedForRequestId.current = requestId;
      handleRequestSearch();
    }
  }, [requestId, searchRequest, isLoading]);

  useEffect(() => {
    if (
      !isFiltersLoading &&
      filters &&
      !isCitiesLoading &&
      cities &&
      !isAgentsLoading
    ) {
      setIsLoading(false);
    }

    if (filtersError) {
      toast.error(errorMessages.filters, {
        error: true,
        action: {},
      });
    }

    if (citiesError) {
      toast.error(errorMessages.countriesOrCitites, {
        error: true,
        action: {},
      });
    }
  }, [
    filters,
    isFiltersLoading,
    cities,
    isCitiesLoading,
    isAgentsLoading,
  ]);

  return (
    <div className="flex gap-1 mb-4 min-h-[100px]">
      {isLoading && <LoaderProgress />}

      <SearchButtonBar />

      {!isLoading && (
        <div className="relative flex gap-2 flex-wrap flex-col w-full">
          <SearchFormTemplate
            key={resetKey}
            initialValues={searchInitialValues}
            handleSubmitForm={handleSubmit}
          >
            {formProps => (
              <div className="flex flex-col gap-2">
                <SearchFormInputs
                  formProps={formProps}
                  handleSettingsClick={handleSettingsClick}
                />

                <SearchFormFilters
                  formProps={formProps}
                  showAdditionalFilters={showAdditionalFilters}
                  handleResetClick={handleResetClick}
                  removedCityId={removedCityId}
                  setRemovedCityId={setRemovedCityId}
                  citiesId={citiesId}
                  inputsAdditional={activeInputsAdditional}
                  inputsMore={activeInputsMore}
                  initialValues={searchInitialValues}
                />

                {showSort && (
                  <div className="flex flex-wrap gap-2 items-center">
                    <InputsTemplate
                      formProps={formProps}
                      inputsList={inputsSort}
                    />

                    {searchRequest && (
                      <div className="flex gap-2 h-fit">
                        <div className="relative">
                          <ButtonTemplate
                            bgColor={
                              activeButton === searchRequestSortButtons.all
                                ? 'bg-redColor border-redColor'
                                : ''
                            }
                            text="All"
                            handleClick={() => handleAllClick(formProps.values)}
                          />
                          <Badge
                            count={totalCount ?? 0}
                            isActive={
                              activeButton === searchRequestSortButtons.all
                            }
                          />
                        </div>

                        <div className="relative">
                          <IconButtonTemplate
                            icon={ThumbsUp}
                            color={
                              activeButton === searchRequestSortButtons.like
                                ? colors.whiteColor
                                : colors.blackColor
                            }
                            classes={
                              activeButton === searchRequestSortButtons.like
                                ? 'bg-redColor border-redColor border-[1px]'
                                : 'border-[1px] border-gray-200'
                            }
                            handleClick={() => handleLikeSortClick('like')}
                          />
                          <Badge
                            count={likeCount}
                            isActive={
                              activeButton === searchRequestSortButtons.like
                            }
                          />
                        </div>

                        <div className="relative">
                          <IconButtonTemplate
                            icon={ThumbsDown}
                            color={
                              activeButton === searchRequestSortButtons.dislike
                                ? colors.whiteColor
                                : colors.blackColor
                            }
                            classes={
                              activeButton === searchRequestSortButtons.dislike
                                ? 'bg-redColor border-redColor border-[1px]'
                                : 'border-[1px] border-gray-200'
                            }
                            handleClick={() => handleLikeSortClick('dislike')}
                          />
                          <Badge
                            count={dislikeCount}
                            isActive={
                              activeButton === searchRequestSortButtons.dislike
                            }
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </SearchFormTemplate>

          <SearchFormClientInfo />
        </div>
      )}
    </div>
  );
};
